import { supabase } from '../../lib/supabase'
import {
  DetailedRosterMember,
  AdminUserItem,
  AdminInvitationItem,
  TrackingStatus,
  TrackingPauseResolution,
} from '../../types/team'

export interface CreateAthleteParams {
  organizationId: string
  teamId: string
  seasonId: string
  firstName: string
  lastName: string
  email?: string
  primaryPosition: string
  secondaryPositions?: string[]
  unit: 'offense' | 'defense' | 'both'
  jerseyNumber?: number
  trackingStartedAt?: string
  invite?: boolean
  allowHomonym?: boolean
}

export interface UpdateAthleteParams {
  athleteId: string
  teamId: string
  seasonId: string
  firstName: string
  lastName: string
  primaryPosition: string
  secondaryPositions?: string[]
  unit: 'offense' | 'defense' | 'both'
  jerseyNumber?: number
}

export const teamService = {
  /**
   * Obtiene la plantilla completa y detallada de un equipo/temporada con estados de seguimiento y onboarding
   */
  async getTeamRosterDetailed(teamId: string, seasonId?: string): Promise<DetailedRosterMember[]> {
    if (!supabase) return []

    const { data, error } = await supabase.rpc('get_team_roster_detailed', {
      p_team_id: teamId,
      p_season_id: seasonId || null,
    })

    if (error) {
      console.error('Error al obtener plantilla detallada:', error)
      throw new Error(error.message)
    }

    return (data as DetailedRosterMember[]) || []
  },

  /**
   * Da de alta un nuevo atleta en la selección y lo añade al grupo de seguimiento
   */
  async createAthleteAndRoster(params: CreateAthleteParams): Promise<{
    success: boolean
    athlete_id: string
    roster_id: string
    invitation_id?: string
    invitation_token?: string
    message: string
  }> {
    if (!supabase) throw new Error('Cliente de base de datos no configurado.')

    const { data, error } = await supabase.rpc('admin_create_athlete_and_roster', {
      p_organization_id: params.organizationId,
      p_team_id: params.teamId,
      p_season_id: params.seasonId,
      p_first_name: params.firstName,
      p_last_name: params.lastName,
      p_email: params.email || null,
      p_primary_position: params.primaryPosition,
      p_secondary_positions: params.secondaryPositions || [],
      p_unit: params.unit,
      p_jersey_number: params.jerseyNumber ?? null,
      p_tracking_started_at: params.trackingStartedAt || new Date().toISOString().split('T')[0],
      p_invite: params.invite ?? false,
      p_allow_homonym: Boolean(params.allowHomonym),
    })

    if (error) {
      throw new Error(error.message)
    }

    return data
  },

  /**
   * Actualiza los datos deportivos de la ficha de un atleta y su registro en plantilla
   */
  async updateAthleteRoster(params: UpdateAthleteParams): Promise<{ success: boolean; message: string }> {
    if (!supabase) throw new Error('Cliente de base de datos no configurado.')

    const { data, error } = await supabase.rpc('admin_update_athlete_roster', {
      p_athlete_id: params.athleteId,
      p_team_id: params.teamId,
      p_season_id: params.seasonId,
      p_first_name: params.firstName,
      p_last_name: params.lastName,
      p_primary_position: params.primaryPosition,
      p_secondary_positions: params.secondaryPositions || [],
      p_unit: params.unit,
      p_jersey_number: params.jerseyNumber ?? null,
    })

    if (error) {
      throw new Error(error.message)
    }

    return data
  },

  /**
   * Cambia el estado de seguimiento: activo, pausado o archivado
   */
  async setTrackingStatus(
    rosterEntryId: string,
    newStatus: TrackingStatus,
    resolution: TrackingPauseResolution = 'keep_pending'
  ): Promise<{ success: boolean; message: string }> {
    if (!supabase) throw new Error('Cliente de base de datos no configurado.')

    const { data, error } = await supabase.rpc('admin_set_tracking_status', {
      p_roster_entry_id: rosterEntryId,
      p_new_status: newStatus,
      p_resolution: resolution,
    })

    if (error) {
      throw new Error(error.message)
    }

    return data
  },

  /**
   * Emite una invitación para un atleta o miembro de staff
   */
  async inviteUser(params: {
    organizationId: string
    teamId: string
    email: string
    roles: string[]
    capabilities?: string[]
    athleteId?: string
  }): Promise<{
    success: boolean
    invitation_id: string
    token: string
    email: string
    roles: string[]
    message: string
  }> {
    if (!supabase) throw new Error('Cliente de base de datos no configurado.')

    const { data, error } = await supabase.rpc('admin_invite_user', {
      p_organization_id: params.organizationId,
      p_team_id: params.teamId,
      p_email: params.email,
      p_roles: params.roles,
      p_capabilities: params.capabilities || [],
      p_athlete_id: params.athleteId || null,
    })

    if (error) {
      throw new Error(error.message)
    }

    return data
  },

  /**
   * Reenvía una invitación previa invalidando el token anterior
   */
  async resendInvitation(invitationId: string): Promise<{
    success: boolean
    invitation_id: string
    token: string
    message: string
  }> {
    if (!supabase) throw new Error('Cliente de base de datos no configurado.')

    const { data, error } = await supabase.rpc('admin_resend_invitation', {
      p_invitation_id: invitationId,
    })

    if (error) {
      throw new Error(error.message)
    }

    return data
  },

  /**
   * Revoca una invitación activa
   */
  async revokeInvitation(invitationId: string): Promise<{ success: boolean; message: string }> {
    if (!supabase) throw new Error('Cliente de base de datos no configurado.')

    const { data, error } = await supabase.rpc('admin_revoke_invitation', {
      p_invitation_id: invitationId,
    })

    if (error) {
      throw new Error(error.message)
    }

    return data
  },

  /**
   * Obtiene la lista de usuarios, roles, permisos e invitaciones de la organización (panel admin)
   */
  async getAdminUsersAndPermissions(organizationId: string): Promise<{
    users: AdminUserItem[]
    invitations: AdminInvitationItem[]
  }> {
    if (!supabase) return { users: [], invitations: [] }

    const { data, error } = await supabase.rpc('get_admin_users_and_permissions', {
      p_organization_id: organizationId,
    })

    if (error) {
      console.error('Error al obtener usuarios y permisos:', error)
      throw new Error(error.message)
    }

    return {
      users: (data?.users as AdminUserItem[]) || [],
      invitations: (data?.invitations as AdminInvitationItem[]) || [],
    }
  },

  /**
   * Concede, actualiza o revoca roles y capacidades de un usuario en un equipo/organización
   */
  async manageUserPermissions(params: {
    userId: string
    organizationId: string
    teamId: string
    roles: string[]
    capabilities: string[]
    isActive: boolean
  }): Promise<{ success: boolean; message: string }> {
    if (!supabase) throw new Error('Cliente de base de datos no configurado.')

    const { data, error } = await supabase.rpc('admin_manage_user_permissions', {
      p_user_id: params.userId,
      p_organization_id: params.organizationId,
      p_team_id: params.teamId,
      p_roles: params.roles,
      p_capabilities: params.capabilities,
      p_is_active: params.isActive,
    })

    if (error) {
      throw new Error(error.message)
    }

    return data
  },
}
